import type { FC } from 'react';
import { lazy, useMemo } from 'react';
import type { StorageLocationsOverviewParams } from 'src/apis/storage/types';
import { usePublicMarketAreas } from 'src/apis/storage/use-public-market-areas';
import { usePublicStorageLocations } from 'src/apis/storage/use-public-storage-locations';
import {
  useStorageServiceRoot,
  type StorageServiceRootRequest,
} from 'src/apis/storage/use-storage-service-root';
import { PageSpinner } from 'src/components/spinner-container';
import type { Successful } from 'src/hooks/use-axios';
import { useStorageLocationsPageParams } from 'src/pages/storage/locations-new/overview/use-page-params';

const Page = lazy(() => import('./page'));

export const StorageLocationsRoot: FC = () => {
  const storageServiceRoot = useStorageServiceRoot();

  if (!storageServiceRoot.response) {
    return <PageSpinner />;
  }

  return (
    <StorageLocationsOverviewNew storageServiceRoot={storageServiceRoot} />
  );
};

type Props = {
  storageServiceRoot: Successful<StorageServiceRootRequest>;
};

export const StorageLocationsOverviewNew: FC<Props> = ({
  storageServiceRoot,
}) => {
  const pageParams = useStorageLocationsPageParams();

  const params = useMemo(() => {
    return {
      sortBy: pageParams.value.sortBy,
      sortDirection: pageParams.value.sortDirection,
      limit: pageParams.value.pageSize,
      offset: pageParams.value.start,
      locationBusinessId: pageParams.value.locationBusinessId,
      name: pageParams.value.name,
      ssoOrganisationId: pageParams.value.ssoOrganisationId.map(
        (sso) => sso.value
      ),
      marketAreaId: pageParams.value.marketAreaId,
      ownLocations: pageParams.value.ownLocations,
    } satisfies StorageLocationsOverviewParams;
  }, [pageParams.value]);

  const storageLocations = usePublicStorageLocations({
    storageServiceRoot,
    params,
  });

  const marketAreas = usePublicMarketAreas({
    storageServiceRoot,
  });

  if (!storageLocations.response || !marketAreas.response)
    return <PageSpinner />;

  return (
    <Page
      storageServiceRoot={storageServiceRoot}
      storageLocations={storageLocations}
      marketAreas={marketAreas}
      pageParams={pageParams}
    />
  );
};
